import T from 'prop-types'
/**
PW Text Input

- save can be called directly through the use of refs...?
update initialValue prop to force this input's value to match
*/
import React from 'react'
import ReactDOM from 'react-dom'

const ENTER_KEY_CODE = 13
const ESC_KEY_CODE = 27

class TextInput extends React.Component {
  static displayName = 'TextInput'

  static propTypes = {
    alwaysSetInitialValue: T.bool, // If true, the initialValue will always be set to state.value, even if it is identical to the prior/current value
    blurOnSave: T.bool,
    cancelOnBlur: T.bool,
    cancelOnEsc: T.bool,
    className: T.string,
    clearOnSave: T.bool,
    'data-testid': T.string,
    dataType: T.oneOf(['string', 'number']),
    disabled: T.bool,
    id: T.string,
    initialValue: T.oneOfType([T.string, T.number]),
    name: T.string,
    onBlur: T.func,
    onCancel: T.func,
    onClick: T.func,
    onFocus: T.func,
    onKeyDown: T.func,
    onKeyUp: T.func,
    onSave: T.func.isRequired,
    onSaveArgs: T.array, // Optional additional arguments to pass with onSave calls. e.g. (<value>, ...onSaveArgs)
    placeholder: T.string,
    readOnly: T.bool,
    rows: T.number, // Textarea rows
    sanitizer: T.func,
    saveOnBlur: T.bool,
    saveOnEnter: T.bool,
    focusOnMount: T.bool,
    saveOnChange: T.bool,
    tooltip: T.string, // Overridden with invalidMessage if that
    trimOnSave: T.bool,
    type: T.string,
    units: T.string,
    validateOnChange: T.bool,
    validator: T.func, // function should return `null` if valid, and a string message if invalid
    autoComplete: T.string,
    saveInvalid: T.bool // If true, will allow invalid values to produce onSave calls
  }

  static defaultProps = {
    alwaysSetInitialValue: false,
    initialValue: '',
    onSaveArgs: [],
    type: 'text',
    saveOnEnter: true,
    saveOnBlur: true,
    blurOnSave: false,
    cancelOnBlur: false,
    focusOnMount: false,
    cancelOnEsc: false,
    clearOnSave: false,
    validateOnChange: false,
    trimOnSave: true,
    saveInvalid: false
  }

  constructor(props, context) {
    super(props, context)

    const val = this._convertToString(props.initialValue)
    this._lastVal = val

    this.state = {
      invalidMessage: null,
      value: val
    }
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    let newVal = newProps.initialValue
    if (newVal !== this.props.initialValue || newProps.alwaysSetInitialValue) {
      newVal = this._convertToString(newVal)
      this.setState({
        value: newVal
      })
      this._lastVal = newVal
    }
  }

  componentWillUnmount() {
    this._unmounted = true
  }

  componentDidMount() {
    const { focusOnMount } = this.props
    if (focusOnMount) {
      this.focus()
    }
  }

  _convertToString = (val) => {
    if (val != null) {
      return '' + val
    } else {
      return ''
    }
  }

  _convertToDataType = (val) => {
    if (this.props.dataType === 'number') {
      val = parseFloat(val)
      return isNaN(val) ? null : val
    } else {
      return val
    }
  }

  _stripUnits = (val) => {
    const units = this.props.units
    if (units && this._valueHasUnits(val)) {
      return val.substring(0, val.length - units.length)
    } else {
      return val
    }
  }

  _addUnits = (val) => {
    const units = this.props.units
    if (val !== '' && units && !this._valueHasUnits(val)) {
      return val + units
    } else {
      return val
    }
  }

  _valueHasUnits = (val) => {
    const units = this.props.units
    if (units) {
      const idx = val.lastIndexOf(units)
      return idx !== -1 && idx === val.length - units.length
    } else {
      return false
    }
  }

  focus = () => {
    if (!this._unmounted) {
      const node = ReactDOM.findDOMNode(this)
      return node != null ? node.focus() : void 0
    }
  }

  setValue = (value, callback) => {
    return this.setState({ value: `${value}` }, callback) // set value as string
  }

  _validate = (inputVal) => {
    const value = inputVal || this._getSaveValue()
    const invalidMessage =
      this.props.validator && typeof this.props.validator === 'function'
        ? this.props.validator(value)
        : null
    this.setState({ invalidMessage }) // Set new validity state (null or error message)
    return invalidMessage
  }

  _getTrimmedValue = () => {
    return this.props.trimOnSave && this.state.value.trim
      ? this.state.value.trim()
      : this.state.value
  }

  _getSaveValue = (value) => {
    let trimmedValue = value || this._getTrimmedValue()

    if (this.props.sanitizer && typeof this.props.sanitizer === 'function') {
      trimmedValue = this.props.sanitizer(trimmedValue)
    }

    const convertedValue = this._convertToDataType(trimmedValue)
    return convertedValue
  }

  save = () => {
    const {
      saveInvalid
    } = this.props
    let value = this._getTrimmedValue()
    const convertedValue = this._getSaveValue(value)
    if (!saveInvalid && this._validate(convertedValue) != null) {
      return // Prevent invalid values from firing onSave
    }

    if (value !== this._lastVal) {
      this.props.onSave(convertedValue, ...this.props.onSaveArgs)
      this._lastVal = value
      if (this.props.clearOnSave) {
        this._lastVal = value = ''
        this.setState({ value })
      } else if (this.props.trimOnSave) {
        this.setState({ value })
      }
    }
    if (this._focused && this.props.blurOnSave) {
      this._ignoreBlur = true
      return ReactDOM.findDOMNode(this).blur()
    }
  }

  cancel = () => {
    this.setState({ value: this._cancelVal })
    if (typeof this.props.onCancel === 'function') {
      this.props.onCancel()
    }
    this._ignoreBlur = true
    ReactDOM.findDOMNode(this).blur()
  }

  forceChange = () => {
    this._handleChange(this.state.value, true)
  }

  _onFocus = (e) => {
    this._focused = true
    this._cancelVal = e.target.value
    if (this.props.units) {
      this.forceUpdate()
    }
    return typeof this.props.onFocus === 'function'
      ? this.props.onFocus()
      : void 0
  }

  _onBlur = () => {
    this._focused = false
    delete this._cancelVal
    if (typeof this.props.onBlur === 'function') {
      this.props.onBlur()
    }
    if (this._ignoreBlur) {
      this._ignoreBlur = false
    } else {
      if (this.props.saveOnBlur) {
        this.save()
      } else if (this.props.cancelOnBlur) {
        this.cancel()
      }
    }
    if (this.props.units) {
      this.forceUpdate()
    }
  }

  _onKeyUp = (e) => {
    const { cancelOnEsc, onKeyUp, saveOnEnter, type } = this.props
    if (saveOnEnter && e.keyCode === ENTER_KEY_CODE && type !== 'textarea') {
      e.preventDefault()
      this.save()
    } else if (cancelOnEsc && e.keyCode === ESC_KEY_CODE) {
      this.cancel()
    }
    return typeof onKeyUp === 'function' ? onKeyUp(e) : void 0
  }

  _onKeyDown = (e) => {
    return typeof this.props.onKeyDown === 'function'
      ? this.props.onKeyDown(e)
      : void 0
  }

  _onChange = (e) => {
    let value = e.target.value
    value = this._stripUnits(value)
    this._handleChange(value)
  }

  _handleChange = (value, forceChange) => {
    const { validateOnChange, saveOnChange } = this.props
    if (value !== this.state.value || forceChange) {
      if (validateOnChange) {
        this._validate(value)
      }
      return this.setState({ value }, () => {
        if (saveOnChange) {
          this.save()
        }
      })
    }
  }

  render() {
    const { props, state } = this
    const {
      className,
      id,
      name,
      disabled,
      readOnly,
      onClick,
      rows,
      placeholder,
      tooltip,
      type,
      autoComplete
    } = props
    const { value, invalidMessage } = state
    const InputElement = type === 'textarea' ? 'textarea' : 'input'

    return (
      <InputElement
        className={`${className} ${invalidMessage
          ? ' pw_text_input_invalid'
          : ''}`}
        data-testid={props['data-testid'] || null}
        data-tooltip={invalidMessage || tooltip}
        disabled={disabled || readOnly}
        id={id}
        name={name}
        onBlur={this._onBlur}
        onChange={this._onChange}
        onClick={onClick}
        onFocus={this._onFocus}
        onKeyDown={this._onKeyDown}
        onKeyUp={this._onKeyUp}
        placeholder={placeholder}
        readOnly={readOnly}
        rows={rows}
        type={type}
        value={this._focused ? value : this._addUnits(value)}
        autoComplete={autoComplete}
      />
    )
  }
}

export default TextInput
